'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useState } from 'react';

export default function ReactQueryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            retry: (failureCount, error) => {
              const err = error as Error;
              // Don't retry if we got a 4xx response
              if (
                err instanceof Error && 
                'response' in err && 
                typeof err.response === 'object' && 
                err.response && 
                'status' in err.response && 
                typeof err.response.status === 'number' && 
                err.response.status >= 400 && 
                err.response.status < 500
              ) {
                return false;
              }
              // Network errors often don't have status codes, so we retry those
              if (err.message && (
                err.message.includes('network') ||
                err.message.includes('connection') ||
                err.message.includes('offline')
              )) {
                // Retry network errors more times
                return failureCount < 5;
              }
              // Retry up to 3 times for other errors
              return failureCount < 3;
            },
            retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
            refetchOnWindowFocus: false, // Disable automatic refetching when window gains focus
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV !== 'production' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
